<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCartStore } from '../stores/cart'
import { useWishlistStore } from '../stores/wishlist'
import { useAuthStore } from '../stores/auth'
import { getCategories, getProducts, type Category, type Product } from '../composables/useStorefrontApi'
import { MagnifyingGlassIcon, UserIcon, HeartIcon, ShoppingCartIcon, PhoneIcon, EnvelopeIcon, Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useSettingsStore } from '../stores/settings'
import { useCurrencyStore } from '../stores/currency'

const router = useRouter()
const cart = useCartStore()
const wishlist = useWishlistStore()
const auth = useAuthStore()
const settingsStore = useSettingsStore()
const currencyStore = useCurrencyStore()
const { t, locale } = useI18n()

const isMenuOpen = ref(false)
const searchQuery = ref('')
const searchResults = ref<Product[]>([])
const isSearching = ref(false)
const showResults = ref(false)
const settings = computed(() => settingsStore.settings)
const categories = ref<Category[]>([])
let searchTimeout: any = null
const animateCart = ref(false)

watch(searchQuery, (newQuery) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  if (!newQuery.trim()) {
    searchResults.value = []
    isSearching.value = false
    return
  }

  isSearching.value = true
  showResults.value = true
  
  searchTimeout = setTimeout(async () => {
    try {
      const result = await getProducts({
        search: newQuery,
        page: 1,
        perPage: 5
      })
      if (result) {
        searchResults.value = result.items
      }
    } catch (error) {
      console.error('Instant search error:', error)
    } finally {
      isSearching.value = false
    }
  }, 300) 
})

const cartCount = computed(() => cart.itemCount)
const wishlistCount = computed(() => wishlist.count)

watch(cartCount, (newVal, oldVal) => {
  if (newVal > oldVal) {
    animateCart.value = true
    setTimeout(() => {
      animateCart.value = false
    }, 500)
  }
})

const languages = [
  { code: 'mk', name: 'МК' },
  { code: 'sq', name: 'SQ' },
  { code: 'en', name: 'EN' },
]

function handleSearch() {
  if (searchQuery.value.trim()) {
    router.push({ name: 'search', query: { q: searchQuery.value } })
    searchQuery.value = ''
    showResults.value = false
    isMenuOpen.value = false
  }
}

function handleBlur() {
  setTimeout(() => {
    showResults.value = false
  }, 200)
}

async function changeLanguage(lang: string) {
  locale.value = lang
  localStorage.setItem('storefront-locale', lang)
  // Reload categories with new locale
  const cats = await getCategories(locale.value)
  categories.value = cats
}

const isCurrencyOpen = ref(false)
const isLanguageOpen = ref(false)

function selectCurrency(code: string) {
  currencyStore.setCurrency(code)
  isCurrencyOpen.value = false
}

function selectLanguage(code: string) {
  changeLanguage(code)
  isLanguageOpen.value = false
}

onMounted(async () => {
  const cats = await getCategories(locale.value)
  categories.value = cats

  // Click outside listener for dropdowns
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (!target.closest('.currency-dropdown')) isCurrencyOpen.value = false
    if (!target.closest('.language-dropdown')) isLanguageOpen.value = false
  })
})
</script>

<template>
  <header class="sticky top-0 z-50 bg-white border-b border-gray-100">
    <!-- Top Bar -->
    <div class="bg-gradient-to-r from-primary-500 to-primary-600 text-white text-xs py-2">
      <div class="container mx-auto px-4 flex justify-between items-center">
        <div class="flex items-center gap-4">
          <a v-if="settings" :href="`tel:${settings.webshop.contactPhone || settings.phone}`" class="flex items-center gap-1.5 hover:text-white/80 transition">
            <PhoneIcon class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">{{ settings.webshop.contactPhone || settings.phone }}</span>
          </a>
          <a v-else href="tel:+38921234567" class="flex items-center gap-1.5 hover:text-white/80 transition">
            <PhoneIcon class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">+389 2 123 4567</span>
          </a>

        </div>
        <div class="flex items-center gap-3">
          <span v-if="settings" class="hidden md:inline text-xs">
            {{ t('header.freeShipping', { threshold: settings.webshop.freeShippingThreshold || 2000, currency: settings.webshop.currency || t('common.currency') }) }}
          </span>
          <span v-else class="hidden md:inline text-xs">{{ t('header.freeShipping', { threshold: 2000, currency: t('common.currency') }) }}</span>


          <!-- Currency Switcher -->
          <div class="relative currency-dropdown border-l border-white/20 pl-4 ml-2">
             <button
              @click="isCurrencyOpen = !isCurrencyOpen"
              class="flex items-center gap-1.5 text-white/90 hover:text-white transition text-xs font-semibold py-1 focus:outline-none"
            >
              <span>{{ currencyStore.currency }}</span>
              <ChevronDownIcon class="w-3 h-3 opacity-70" :class="{ 'rotate-180': isCurrencyOpen }" />
            </button>

            <!-- Dropdown Menu -->
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div v-if="isCurrencyOpen" class="absolute right-0 top-full mt-2 w-24 bg-white rounded-xl shadow-xl py-1 z-50 overflow-hidden ring-1 ring-black ring-opacity-5">
                 <button
                  v-for="curr in currencyStore.availableCurrencies"
                  :key="curr.code"
                  @click="selectCurrency(curr.code)"
                  class="w-full text-left px-4 py-2 text-xs flex items-center justify-between group transition-colors"
                  :class="currencyStore.currency === curr.code ? 'bg-primary-50 text-primary-600 font-bold' : 'text-gray-700 hover:bg-gray-50'"
                >
                  <span>{{ curr.code }}</span>
                  <span v-if="currencyStore.currency === curr.code" class="text-primary-600">✓</span>
                </button>
              </div>
            </transition>
          </div>

          <!-- Language Switcher -->
          <div class="relative language-dropdown">
            <button
              @click="isLanguageOpen = !isLanguageOpen"
              class="flex items-center gap-1.5 text-white/90 hover:text-white transition text-xs font-semibold py-1 focus:outline-none"
            >
              <span>{{ languages.find(l => l.code === locale)?.name || locale.toUpperCase() }}</span>
              <ChevronDownIcon class="w-3 h-3 opacity-70" :class="{ 'rotate-180': isLanguageOpen }" />
            </button>

             <!-- Dropdown Menu -->
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div v-if="isLanguageOpen" class="absolute right-0 top-full mt-2 w-24 bg-white rounded-xl shadow-xl py-1 z-50 overflow-hidden ring-1 ring-black ring-opacity-5">
                <button
                  v-for="lang in languages"
                  :key="lang.code"
                  @click="selectLanguage(lang.code)"
                  class="w-full text-left px-4 py-2 text-xs flex items-center justify-between group transition-colors"
                   :class="locale === lang.code ? 'bg-primary-50 text-primary-600 font-bold' : 'text-gray-700 hover:bg-gray-50'"
                >
                  <span>{{ lang.name }}</span>
                   <span v-if="locale === lang.code" class="text-primary-600">✓</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white">
      <div class="container mx-auto px-4 py-5">
      <div class="flex items-center justify-between gap-4">
        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-3 group">
          <div v-if="settings?.logo" class="h-12 w-auto max-w-[200px] flex items-center">
            <img 
              :src="settings.logo" 
              :alt="settings?.storeName || 'Logo'"
              class="h-full w-auto object-contain"
            />
          </div>
          <div v-else class="flex items-center gap-2.5">
            <div class="w-11 h-11 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition">
              <span class="text-white text-2xl font-bold">⚡</span>
            </div>
            <span class="text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition">{{ settings?.storeName || settings?.webshop?.storeName || 'Click Electra' }}</span>
          </div>
        </router-link>

        <div class="hidden md:flex flex-1 max-w-xl mx-8 relative group">
          <form @submit.prevent="handleSearch" class="w-full relative">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('header.searchPlaceholder')"
              class="w-full pl-5 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all"
              @focus="showResults = true"
              @blur="handleBlur"
            />
            <button
              type="submit"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition shadow-sm z-10"
            >
              <MagnifyingGlassIcon class="w-4 h-4" />
            </button>
          </form>

          <!-- Instant Search Results -->
          <div 
            v-if="showResults && (searchResults.length > 0 || isSearching)"
            class="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[400px] overflow-y-auto"
          >
            <div v-if="isSearching" class="p-4 text-center text-gray-500">
              <div class="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              Entering...
            </div>
            <div v-else>
              <router-link
                v-for="product in searchResults"
                :key="product.id"
                :to="`/products/${product.id}`"
                class="flex items-center gap-4 p-3 hover:bg-gray-50 transition border-b last:border-0"
                @click="showResults = false"
              >
                <div class="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    v-if="product.imageUrl" 
                    :src="product.imageUrl" 
                    :alt="product.name"
                    class="w-full h-full object-contain bg-white"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center text-lg">📦</div>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 truncate">{{ product.name }}</div>
                  <div class="text-sm text-primary-600 font-bold">{{ currencyStore.format(product.sellingPrice) }}</div>
                </div>
              </router-link>
              <div v-if="searchResults.length === 0 && searchQuery" class="p-4 text-center text-gray-500">
                {{ t('search.noResults', { query: searchQuery }) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-4">
          <!-- Login / Account -->
          <router-link v-if="!auth.isLoggedIn()" to="/login" class="hidden md:flex flex-col items-center gap-1 text-gray-600 hover:text-primary-600 transition group">
            <div class="p-2 rounded-lg group-hover:bg-primary-50 transition">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
            </div>
            <span class="text-xs">{{ t('header.login') || 'Login' }}</span>
          </router-link>

          <router-link v-else to="/account" class="hidden md:flex flex-col items-center gap-1 text-gray-600 hover:text-primary-600 transition group">
            <div class="p-2 rounded-lg group-hover:bg-primary-50 transition">
              <UserIcon class="w-5 h-5" />
            </div>
            <span class="text-xs">{{ t('header.account') || 'Account' }}</span>
          </router-link>



          <!-- Wishlist -->
          <!-- Wishlist -->
          <router-link 
            v-if="settings?.webshop?.enableWishlist !== false"
            to="/wishlist" 
            class="hidden md:flex flex-col items-center gap-1 text-gray-600 hover:text-primary-600 transition group relative"
          >
            <div class="p-2 rounded-lg group-hover:bg-primary-50 transition relative">
              <HeartIcon class="w-5 h-5" />
              <span
                v-if="wishlistCount > 0"
                class="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center shadow-md"
              >
                {{ wishlistCount }}
              </span>
            </div>
            <span class="text-xs">{{ t('header.wishlist') }}</span>
          </router-link>

          <!-- Cart -->
          <router-link to="/cart" class="flex flex-col items-center gap-1 text-gray-600 hover:text-primary-600 transition group relative">
            <div class="p-2 rounded-lg group-hover:bg-primary-50 transition relative">
              <ShoppingCartIcon class="w-5 h-5 transition-transform" :class="{ 'scale-125 text-primary-600': animateCart }" />
              <span
                v-if="cartCount > 0"
                class="absolute -top-1 -right-1 bg-primary-600 text-white text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center shadow-md"
              >
                {{ cartCount }}
              </span>
            </div>
            <span class="text-xs">{{ t('header.cart') }}</span>
          </router-link>

          <!-- Mobile Menu Toggle -->
          <button
            @click="isMenuOpen = !isMenuOpen"
            class="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <Bars3Icon v-if="!isMenuOpen" class="w-6 h-6" />
            <XMarkIcon v-else class="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  </div>

    <!-- Navigation -->
    <nav class="hidden lg:block border-t bg-white">
      <div class="container mx-auto px-4">
        <ul class="flex items-center gap-1 py-2">
          <li>
            <router-link to="/" class="px-3 xl:px-4 py-3 text-sm xl:text-base text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium rounded-lg transition whitespace-nowrap">
              {{ t('nav.home') }}
            </router-link>
          </li>
          <li>
            <router-link to="/products" class="px-3 xl:px-4 py-3 text-sm xl:text-base text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium rounded-lg transition whitespace-nowrap">
              {{ t('nav.products') }}
            </router-link>
          </li>
          <li v-if="categories.length > 0" class="relative group">
            <span class="px-3 xl:px-4 py-3 text-sm xl:text-base text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium cursor-pointer rounded-lg transition block whitespace-nowrap">
              {{ t('nav.categories') }}
            </span>
            <div class="absolute left-0 top-full hidden group-hover:block bg-white shadow-xl rounded-lg py-2 min-w-[200px] z-50 border border-gray-100 -mt-px">
              <router-link
                v-for="category in categories"
                :key="category.id"
                :to="`/category/${category.slug}`"
                class="block px-4 py-2.5 hover:bg-primary-50 text-gray-700 hover:text-primary-600 transition"
              >
                {{ category.name }}
              </router-link>
            </div>
          </li>
          <li>
            <router-link to="/track-service" class="px-3 xl:px-4 py-3 text-sm xl:text-base text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium rounded-lg transition whitespace-nowrap">
              {{ t('nav.trackService') || 'Track Service' }}
            </router-link>
          </li>
          <li>
            <router-link to="/about" class="px-3 xl:px-4 py-3 text-sm xl:text-base text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium rounded-lg transition whitespace-nowrap">
              {{ t('nav.about') }}
            </router-link>
          </li>
          <li>
            <router-link to="/contact" class="px-3 xl:px-4 py-3 text-sm xl:text-base text-gray-700 hover:text-primary-600 hover:bg-primary-50 font-medium rounded-lg transition whitespace-nowrap">
              {{ t('nav.contact') }}
            </router-link>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Mobile Menu -->
    <div
      v-if="isMenuOpen"
      class="lg:hidden absolute top-full left-0 right-0 bg-white shadow-2xl border-t z-40 max-h-[85vh] overflow-y-auto overscroll-contain pb-10"
    >
      <div class="container mx-auto px-4 py-4">
        <form @submit.prevent="handleSearch" class="mb-4">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('header.searchPlaceholderMobile')"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg"
            @focus="showResults = true"
            @blur="handleBlur"
          />
        </form>

        <!-- Mobile Search Results -->
        <div 
          v-if="showResults && (searchResults.length > 0 || isSearching)"
          class="mb-4 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden max-h-[300px] overflow-y-auto"
        >
          <div v-if="isSearching" class="p-4 text-center text-gray-500">
            <div class="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            Searching...
          </div>
          <div v-else>
            <router-link
              v-for="product in searchResults"
              :key="product.id"
              :to="`/products/${product.id}`"
              class="flex items-center gap-3 p-3 hover:bg-gray-100 transition border-b border-gray-200 last:border-0"
              @click="{ showResults = false; isMenuOpen = false }"
            >
              <div class="w-10 h-10 bg-white rounded-md overflow-hidden flex-shrink-0 border border-gray-200">
                <img 
                  v-if="product.imageUrl" 
                  :src="product.imageUrl" 
                  :alt="product.name"
                  class="w-full h-full object-contain p-0.5"
                />
                <div v-else class="w-full h-full flex items-center justify-center text-sm">📦</div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-medium text-gray-900 text-sm truncate">{{ product.name }}</div>
                <div class="text-xs text-primary-600 font-bold">{{ currencyStore.format(product.sellingPrice) }}</div>
              </div>
            </router-link>
            <div v-if="searchResults.length === 0 && searchQuery" class="p-3 text-center text-sm text-gray-500">
              {{ t('search.noResults', { query: searchQuery }) }}
            </div>
          </div>
        </div>
        <!-- Language Switcher Mobile -->
        <div class="mb-4 pb-4 border-b">
          <div class="flex items-center gap-2 justify-center">
            <div class="flex gap-2">
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="changeLanguage(lang.code)"
                :class="[
                  'px-3 py-1 rounded text-sm transition',
                  locale === lang.code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                ]"
              >
                {{ lang.name }}
              </button>
            </div>
          </div>
        </div>
        <nav>
          <!-- Login / Account for Mobile -->
          <router-link 
            v-if="!auth.isLoggedIn()" 
            to="/login" 
            class="flex items-center gap-2 py-3 border-b font-medium text-primary-600" 
            @click="isMenuOpen = false"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
            </svg>
            {{ t('header.login') || 'Login' }}
          </router-link>
          <router-link 
            v-else 
            to="/account" 
            class="flex items-center gap-2 py-3 border-b font-medium text-primary-600" 
            @click="isMenuOpen = false"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            {{ t('header.account') || 'Account' }}
          </router-link>

          <!-- Wishlist for Mobile -->
          <!-- Wishlist for Mobile -->
          <router-link 
            v-if="settings?.webshop?.enableWishlist !== false"
            to="/wishlist" 
            class="flex items-center gap-2 py-3 border-b" 
            @click="isMenuOpen = false"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
            {{ t('header.wishlist') }}
            <span v-if="wishlistCount > 0" class="ml-auto bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
              {{ wishlistCount }}
            </span>
          </router-link>

          <router-link to="/" class="block py-3 px-2 border-b text-gray-700 font-medium hover:bg-gray-50 rounded" @click="isMenuOpen = false">{{ t('nav.home') }}</router-link>
          <router-link to="/products" class="block py-3 px-2 border-b text-gray-700 font-medium hover:bg-gray-50 rounded" @click="isMenuOpen = false">{{ t('nav.products') }}</router-link>
          <template v-if="categories.length > 0">
            <div class="border-b py-2">
              <div class="px-2 py-2 font-bold text-gray-900">{{ t('nav.categories') }}</div>
              <div class="pl-2 space-y-1">
                <router-link
                  v-for="category in categories"
                  :key="category.id"
                  :to="`/category/${category.slug}`"
                  class="block py-2 px-3 text-sm text-gray-600 hover:text-primary-600 hover:bg-gray-50 rounded transition"
                  @click="isMenuOpen = false"
                >
                  {{ category.name }}
                </router-link>
              </div>
            </div>
          </template>
          <router-link to="/about" class="block py-3 px-2 border-b text-gray-700 font-medium hover:bg-gray-50 rounded" @click="isMenuOpen = false">{{ t('nav.about') }}</router-link>
          <router-link to="/track-service" class="block py-3 px-2 border-b text-gray-700 font-medium hover:bg-gray-50 rounded" @click="isMenuOpen = false">{{ t('nav.trackService') || 'Track Service' }}</router-link>
          <router-link to="/contact" class="block py-3 px-2 text-gray-700 font-medium hover:bg-gray-50 rounded" @click="isMenuOpen = false">{{ t('nav.contact') }}</router-link>
        </nav>
      </div>
    </div>
  </header>
</template>


